import React, { createContext, useContext, useState } from 'react';
import api from '../middleware/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const CartContext = createContext();

export const useCart = () => {
    return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);
    const { user } = useAuth();
    const [paymentUrl, setPaymentUrl] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addToCart = (product, quantity = 1) => {
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === product.id);
            if (existingItem) {
                return prevCart.map(item =>
                    item.id === product.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...prevCart, { ...product, quantity }];
        });
    };

    const removeFromCart = (productId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, quantity) => {
        if (quantity < 1) return;
        setCart(prevCart =>
            prevCart.map(item =>
                item.id === productId ? { ...item, quantity } : item
            )
        );
    };

    const getTotal = () => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const resetCart = () => {
        setCart([]);
    };

    const refillCapsules = async (addressData) => {
        if (!user) {
            toast.error('Oops, une erreur est survenue lors de la création de la commande de recharge');
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                lineItems: cart.map(item => ({
                    productId: item.wooProductId,
                    quantity: item.quantity,
                })),
                addresses: addressData.addresses,
                shippingAddressId: addressData.shippingAddressId,
                billingAddressId: addressData.billingAddressId,
            };

            const response = await api.post('/orders/account/refill', { order: payload });
            if (response.data.success) {
                console.log('Refill success:', response.data);
                setPaymentUrl(response.data.order.paymentUrl);
                resetCart();
            } else {
                console.error('Refill error:', response.data.message);
                toast.error(response.data.message || 'Oops, une erreur est survenue lors de la création de la commande de recharge');
            }
        } catch (error) {
            console.error('Refill error:', error);
            toast.error('Oops, une erreur est survenue lors de la création de la commande de recharge');
            throw error; // Propager l'erreur pour que Products.jsx puisse la gérer
        } finally {
            setIsSubmitting(false);
        }
    };

    const orderProducts = async (data) => {
        if (!user) {
            toast.error('Oops, une erreur est survenue lors de la création de la commande');
            return;
        }
        try {
            setIsSubmitting(true);
            const payload = {
                lineItems: cart.map(item => ({
                    productId: item.wooProductId,
                    quantity: item.quantity,
                })),
                addresses: data.addresses,
                shippingAddressId: data.shippingAddressId,
                billingAddressId: data.billingAddressId,
                // Pour les produits, le thérapeute est à la fois destinataire et payeur
                shippingOwnerId: user.id,
                billingOwnerId: user.id,
            }
            console.log('Payload', payload)
            const response = await api.post('/orders/products', { orderDetails: payload });
            if (response.data.success) {
                console.log('Order success:', response.data);
                toast.success('Commande créée avec succès');
                setPaymentUrl(response.data.order.paymentUrl);
                resetCart();
            } else {
                console.error('Order error:', response.data.message);
                toast.error(response.data.message || 'Oops, une erreur est survenue lors de la création de la commande');
            }
        } catch (error) {
            console.error('Order error:', error);
            toast.error('Oops, une erreur est survenue lors de la création de la commande');
            throw error; // Propager l'erreur pour que Products.jsx puisse la gérer
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <CartContext.Provider value={{ user, cart, addToCart, removeFromCart, updateQuantity, getTotal, resetCart, refillCapsules, orderProducts, paymentUrl, isSubmitting }}>
            {children}
        </CartContext.Provider>
    );
};
